import React, { useState, useEffect, useCallback } from 'react';
import {
  Download,
  FileText,
  Calendar,
  Building,
  Filter,
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowRightLeft,
  Eye,
  X,
  RefreshCcw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../config/api';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { Branch } from '../types/api';
import { limitCsvRows, parseCsv } from '../utils/csv';

type ReportKind = 'abonos' | 'pagos' | 'transferencias';
type PhoneVariant = 'with-phone' | 'without-phone';
type DownloadKey = `${ReportKind}-${PhoneVariant}`;

const PREVIEW_ROW_LIMIT = 100;

const reportKinds: ReportKind[] = ['abonos', 'pagos', 'transferencias'];

const reportConfig: Record<
  ReportKind,
  {
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    iconBg: string;
    iconColor: string;
    withPhoneColumns: string[];
    withoutPhoneColumns: string[];
  }
> = {
  abonos: {
    title: 'Abonos',
    description: 'Depósitos y entradas de saldo a cuentas de clientes',
    icon: ArrowDownCircle,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    withPhoneColumns: ['Teléfono', 'Monto', 'Autorización', 'Sucursal', 'Fecha (México)'],
    withoutPhoneColumns: ['Monto', 'Autorización', 'Sucursal', 'Fecha (México)'],
  },
  pagos: {
    title: 'Pagos',
    description: 'Retiros, compras y salidas de saldo',
    icon: ArrowUpCircle,
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
    withPhoneColumns: ['Teléfono', 'Monto', 'Autorización', 'Sucursal', 'Fecha (México)'],
    withoutPhoneColumns: ['Monto', 'Autorización', 'Sucursal', 'Fecha (México)'],
  },
  transferencias: {
    title: 'Transferencias externas',
    description: 'Solicitudes de transferencia a cuentas externas (SPEI)',
    icon: ArrowRightLeft,
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-600',
    withPhoneColumns: [
      'Referencia',
      'Monto',
      'Emisor',
      'Teléfono emisor',
      'Beneficiario',
      'Banco',
      'CLABE',
      'Estado',
      'Fecha (México)',
    ],
    withoutPhoneColumns: [
      'Referencia',
      'Monto',
      'Beneficiario',
      'Banco',
      'CLABE',
      'Estado',
      'Fecha (México)',
    ],
  },
};

const getReportEndpoint = (kind: ReportKind, variant: PhoneVariant) => {
  const base =
    variant === 'with-phone' ? '/admin/reportes/csv' : '/admin/reportes/csv-sin-telefono';
  return `${base}/${kind}`;
};

const variantLabels: Record<PhoneVariant, string> = {
  'with-phone': 'Con teléfono',
  'without-phone': 'Sin teléfono',
};

type ReportPreview = {
  kind: ReportKind;
  variant: PhoneVariant;
  headers: string[];
  rows: string[][];
  totalRows: number;
  truncated: boolean;
};

const ReportsSection: React.FC = () => {
  const { accessToken, isAuthenticated } = useAuth();
  const { getBranches } = useAdminAPI();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);
  const [filters, setFilters] = useState({
    sucursal: 'all',
    fechaInicio: '',
    fechaFin: '',
  });
  const [downloadingKey, setDownloadingKey] = useState<DownloadKey | null>(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState<DownloadKey | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReportPreview | null>(null);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        setIsLoadingBranches(true);
        const branchesData = await getBranches();
        setBranches(branchesData);
      } catch (error) {
        console.error('Error loading branches:', error);
        setBranches([]);
      } finally {
        setIsLoadingBranches(false);
      }
    };

    if (isAuthenticated) {
      loadBranches();
    }
  }, [isAuthenticated, getBranches]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildQueryString = useCallback(
    (kind: ReportKind) => {
      const params = new URLSearchParams();

      if (filters.sucursal && filters.sucursal !== 'all' && kind !== 'transferencias') {
        params.append('sucursal', filters.sucursal);
      }

      if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
      if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);

      return params.toString();
    },
    [filters]
  );

  const fetchReportCsv = useCallback(
    async (kind: ReportKind, variant: PhoneVariant): Promise<string> => {
      if (!isAuthenticated || !accessToken) {
        throw new Error('Debes estar autenticado para consultar reportes');
      }

      const baseUrl = getApiBaseUrl();
      const endpoint = `${baseUrl}${getReportEndpoint(kind, variant)}`;
      const queryString = buildQueryString(kind);
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/csv,application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('El reporte está vacío. No hay datos con los filtros seleccionados.');
      }

      return blob.text();
    },
    [accessToken, buildQueryString, isAuthenticated]
  );

  const downloadReport = async (kind: ReportKind, variant: PhoneVariant) => {
    const downloadKey: DownloadKey = `${kind}-${variant}`;
    setDownloadingKey(downloadKey);

    try {
      const text = await fetchReportCsv(kind, variant);
      const phoneSuffix = variant === 'with-phone' ? 'con-telefono' : 'sin-telefono';
      const fileName = `reporte-${kind}-${phoneSuffix}-${new Date().toISOString().split('T')[0]}.csv`;

      const newBlob = new Blob([text], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(newBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      alert(
        `Error al descargar el reporte de ${reportConfig[kind].title}: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`
      );
    } finally {
      setDownloadingKey(null);
    }
  };

  const loadPreview = async (kind: ReportKind, variant: PhoneVariant) => {
    const previewKey: DownloadKey = `${kind}-${variant}`;
    setPreviewLoadingKey(previewKey);
    setPreviewError(null);

    try {
      const text = await fetchReportCsv(kind, variant);
      const { headers, rows } = parseCsv(text);
      const { rows: limitedRows, truncated, totalRows } = limitCsvRows(rows, PREVIEW_ROW_LIMIT);

      setPreview({
        kind,
        variant,
        headers,
        rows: limitedRows,
        totalRows,
        truncated,
      });
    } catch (error) {
      console.error('Error al cargar vista previa:', error);
      setPreview(null);
      setPreviewError(
        error instanceof Error
          ? error.message
          : 'No se pudo cargar la vista previa del reporte.'
      );
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const closePreview = () => {
    setPreview(null);
    setPreviewError(null);
  };

  const clearFilters = () => {
    setFilters({
      sucursal: 'all',
      fechaInicio: '',
      fechaFin: '',
    });
  };

  const hasActiveFilters =
    filters.sucursal !== 'all' || filters.fechaInicio !== '' || filters.fechaFin !== '';

  const isPreviewActive = (kind: ReportKind, variant: PhoneVariant) =>
    preview?.kind === kind && preview?.variant === variant;

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-10 w-10 bg-primary-50 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-secondary-900">Reportes del Sistema</h2>
            <p className="text-sm text-secondary-500">
              Vista previa y descarga de reportes CSV por abonos, pagos y transferencias externas
            </p>
          </div>
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-medium text-secondary-900">Filtros de Reporte</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <Building className="h-4 w-4 inline mr-1" />
              Sucursal
            </label>
            <select
              name="sucursal"
              className="input-field"
              value={filters.sucursal}
              onChange={handleFilterChange}
              disabled={isLoadingBranches}
            >
              <option value="all">Todas las sucursales</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name} {branch.alias ? `(${branch.alias})` : ''}
                </option>
              ))}
            </select>
            {isLoadingBranches && (
              <p className="text-xs text-secondary-500 mt-1">Cargando sucursales...</p>
            )}
            <p className="text-xs text-secondary-500 mt-1">
              El filtro de sucursal aplica a abonos y pagos (no a transferencias externas).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Fecha Inicio
            </label>
            <input
              type="date"
              name="fechaInicio"
              className="input-field"
              value={filters.fechaInicio}
              onChange={handleFilterChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Fecha Fin
            </label>
            <input
              type="date"
              name="fechaFin"
              className="input-field"
              value={filters.fechaFin}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mt-4 pt-4 border-t border-secondary-200">
          <div className="text-sm text-secondary-600">
            {hasActiveFilters && (
              <span className="flex items-center">
                <Filter className="h-4 w-4 mr-1" />
                Filtros activos
              </span>
            )}
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {reportKinds.map((kind) => {
          const config = reportConfig[kind];
          const Icon = config.icon;
          const withPhoneKey: DownloadKey = `${kind}-with-phone`;
          const withoutPhoneKey: DownloadKey = `${kind}-without-phone`;
          const isDownloadingWithPhone = downloadingKey === withPhoneKey;
          const isDownloadingWithoutPhone = downloadingKey === withoutPhoneKey;
          const isPreviewingWithPhone = previewLoadingKey === withPhoneKey;
          const isPreviewingWithoutPhone = previewLoadingKey === withoutPhoneKey;
          const isBusy = downloadingKey !== null || previewLoadingKey !== null;

          return (
            <div key={kind} className="card p-6 flex flex-col">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className={`h-12 w-12 ${config.iconBg} rounded-lg flex items-center justify-center`}
                >
                  <Icon className={`h-6 w-6 ${config.iconColor}`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">{config.title}</h3>
                  <p className="text-sm text-secondary-500">{config.description}</p>
                </div>
              </div>
              <div className="text-sm text-secondary-600 mb-4 flex-1 space-y-3">
                <div>
                  <p className="font-medium mb-1 text-xs uppercase tracking-wide text-secondary-500">
                    Con teléfono / datos de contacto
                  </p>
                  <ul className="space-y-0.5 text-xs">
                    {config.withPhoneColumns.map((col) => (
                      <li key={col}>• {col}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-1 text-xs uppercase tracking-wide text-secondary-500">
                    Sin teléfono (anonimizado)
                  </p>
                  <ul className="space-y-0.5 text-xs">
                    {config.withoutPhoneColumns.map((col) => (
                      <li key={col}>• {col}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="space-y-2 mt-auto">
                <button
                  onClick={() => loadPreview(kind, 'with-phone')}
                  disabled={isBusy}
                  className={`btn-secondary w-full flex justify-center items-center text-sm ${
                    isPreviewActive(kind, 'with-phone') ? 'ring-2 ring-primary-400' : ''
                  }`}
                >
                  {isPreviewingWithPhone ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-400 mr-2" />
                      Cargando preview...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview con teléfono
                    </>
                  )}
                </button>
                <button
                  onClick={() => loadPreview(kind, 'without-phone')}
                  disabled={isBusy}
                  className={`btn-secondary w-full flex justify-center items-center text-sm ${
                    isPreviewActive(kind, 'without-phone') ? 'ring-2 ring-primary-400' : ''
                  }`}
                >
                  {isPreviewingWithoutPhone ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-400 mr-2" />
                      Cargando preview...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview sin teléfono
                    </>
                  )}
                </button>
                <button
                  onClick={() => downloadReport(kind, 'with-phone')}
                  disabled={isBusy}
                  className="btn-primary w-full flex justify-center items-center text-sm"
                >
                  {isDownloadingWithPhone ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Descargando...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      CSV con teléfono
                    </>
                  )}
                </button>
                <button
                  onClick={() => downloadReport(kind, 'without-phone')}
                  disabled={isBusy}
                  className="btn-secondary w-full flex justify-center items-center text-sm"
                >
                  {isDownloadingWithoutPhone ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-400 mr-2" />
                      Descargando...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      CSV sin teléfono
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {(preview || previewError) && (
        <div className="card p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary-600" />
                Vista previa del reporte
              </h3>
              {preview && (
                <p className="text-sm text-secondary-500 mt-1">
                  {reportConfig[preview.kind].title} · {variantLabels[preview.variant]}
                  {hasActiveFilters && ' · con filtros aplicados'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {preview && (
                <>
                  <button
                    type="button"
                    onClick={() => loadPreview(preview.kind, preview.variant)}
                    disabled={previewLoadingKey !== null}
                    className="btn-secondary inline-flex items-center text-sm"
                  >
                    <RefreshCcw
                      className={`h-4 w-4 mr-2 ${previewLoadingKey ? 'animate-spin' : ''}`}
                    />
                    Actualizar
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadReport(preview.kind, preview.variant)}
                    disabled={downloadingKey !== null}
                    className="btn-primary inline-flex items-center text-sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descargar CSV
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={closePreview}
                className="btn-secondary inline-flex items-center text-sm"
                aria-label="Cerrar vista previa"
              >
                <X className="h-4 w-4 mr-1" />
                Cerrar
              </button>
            </div>
          </div>

          {previewError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {previewError}
            </div>
          )}

          {preview && !previewError && (
            <>
              <p className="text-sm text-secondary-600">
                {preview.totalRows === 0
                  ? 'Sin registros para los filtros seleccionados.'
                  : preview.truncated
                    ? `Mostrando las primeras ${preview.rows.length} de ${preview.totalRows} filas. Descarga el CSV para ver el reporte completo.`
                    : `${preview.totalRows} fila${preview.totalRows !== 1 ? 's' : ''} en total.`}
              </p>

              {preview.headers.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-secondary-200 max-h-[28rem] overflow-y-auto">
                  <table className="min-w-full divide-y divide-secondary-200 text-sm">
                    <thead className="bg-secondary-50 sticky top-0 z-10">
                      <tr>
                        {preview.headers.map((header, index) => (
                          <th
                            key={`${header}-${index}`}
                            className="px-3 py-2 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider whitespace-nowrap"
                          >
                            {header || `Col ${index + 1}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-100">
                      {preview.rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={preview.headers.length}
                            className="px-4 py-8 text-center text-secondary-500"
                          >
                            No hay datos en este reporte.
                          </td>
                        </tr>
                      ) : (
                        preview.rows.map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-secondary-50">
                            {preview.headers.map((_, colIndex) => (
                              <td
                                key={colIndex}
                                className="px-3 py-2 text-secondary-800 whitespace-nowrap max-w-xs truncate"
                                title={row[colIndex] ?? ''}
                              >
                                {row[colIndex] ?? '—'}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-secondary-500">El CSV no contiene encabezados válidos.</p>
              )}
            </>
          )}
        </div>
      )}

      <div className="card p-6">
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-primary-800 mb-2">
            Información sobre los Reportes
          </h4>
          <ul className="text-sm text-primary-700 space-y-1">
            <li>• Usa Preview para ver el reporte en el dashboard antes de descargarlo</li>
            <li>• La vista previa muestra hasta {PREVIEW_ROW_LIMIT} filas; el CSV completo al descargar</li>
            <li>• Abonos, pagos y transferencias externas se consultan por separado</li>
            <li>• Sucursal aplica a abonos y pagos; transferencias solo usan filtro de fechas</li>
            <li>• Las fechas son filtros opcionales (formato YYYY-MM-DD)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ReportsSection;
